import { useEffect, useState } from 'react';
import { Typography, Grid } from '@material-ui/core';
import {
  InfoCard,
  Header,
  Page,
  Content,
  ContentHeader,
  HeaderLabel,
  SupportButton,
  Progress,
} from '@backstage/core-components';
import { ExampleFetchComponent } from '../ExampleFetchComponent';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

export const ExampleComponent = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [healthData, setHealthData] = useState<{ status: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [awsData, setAwsData] = useState<{ 
    taskDefinitions: string[];
    credentials: {
      accessKeyId: string;
      secretAccessKey: string;
      sessionToken: string;
    }
  } | null>(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('caitlyn');
        const response = await fetchApi.fetch(`${baseUrl}/health`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch health data: ${response.statusText}`);
        }
        
        const data = await response.json();
        setHealthData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setLoading(false);
      }
    };

    const fetchAwsData = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('caitlyn');
        // Create a URL with query parameters
        const url = new URL(`${baseUrl}/aws`);
        url.searchParams.append('awsAccountId', '612526785717'); // Replace with actual AWS account ID
        
        const response = await fetchApi.fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch AWS data: ${response.statusText}`);
        }
        
        const data = await response.json();
        setAwsData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
    fetchAwsData();
  }, [discoveryApi, fetchApi]);

  return (
    <Page themeId="tool">
      <Header title="Welcome to caitlyn!" subtitle="Optional subtitle">
        <HeaderLabel label="Owner" value="Team X" />
        <HeaderLabel label="Lifecycle" value="Alpha" />
      </Header>
      <Content>
        <ContentHeader title="Plugin title">
          <SupportButton>A description of your plugin goes here.</SupportButton>
        </ContentHeader>
        <Grid container spacing={3} direction="column">
          <Grid item>
            <InfoCard title="Backend Health Status">
              {loading ? (
                <Progress />
              ) : error ? (
                <Typography color="error">Error: {error.message}</Typography>
              ) : healthData ? (
                <Typography variant="body1">
                  Status: {healthData.status}<br />
                  Message: {healthData.message}
                </Typography>
              ) : (
                <Typography variant="body1">No data available</Typography>
              )}
            </InfoCard>
          </Grid>
          <Grid item>
            <InfoCard title="AWS ECS Task Definitions">
              {loading ? (
                <Progress />
              ) : error ? (
                <Typography color="error">Error: {error.message}</Typography>
              ) : awsData ? (
                <div>
                  <Typography variant="h6">Found {awsData.taskDefinitions.length} task definitions</Typography>
                  <ul>
                    {awsData.taskDefinitions.map((taskDef, index) => (
                      <li key={index}><Typography variant="body2">{taskDef}</Typography></li>
                    ))}
                  </ul>
                </div>
              ) : (
                <Typography variant="body1">No AWS data available</Typography>
              )}
            </InfoCard>
          </Grid>
          <Grid item>
            <InfoCard title="Information card">
              <Typography variant="body1">
                All content should be wrapped in a card like this.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item>
            <ExampleFetchComponent />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}
